import type Listing from 'data/listing';
import { isServerSide, getInsightsHost } from './host-config';
import { ZoopraisalRequest, ZoopraisalRequest_Coordinates, ZoopraisalResponse } from '@zoocasa/go-search';
import { fetchWithRetry } from './fetchWithRetry';
import { STATUS_ID_NOT_AVAILABLE_SOLD } from 'components/home-appraisal/results';

export interface Zoopraisal {
  averageDaysOnMarket: number | null;
  averagePrice: number | null;
  datasetAddresses: string[];
  datasetCount: number;
  highestPrice: number;
  lowestPrice: number;
  overAskingCount: number;
  isAvailable: boolean;
}

export class ZoopraisalData implements Zoopraisal {
  constructor(private zoopraisal: Zoopraisal) { }
  public get averageDaysOnMarket() { return this.zoopraisal.averageDaysOnMarket; }
  public get averagePrice() { return this.zoopraisal.averagePrice; }
  public get datasetAddresses() { return this.zoopraisal.datasetAddresses; }
  public get datasetCount() { return this.zoopraisal.datasetCount; }
  public get highestPrice() { return this.zoopraisal.highestPrice; }
  public get lowestPrice() { return this.zoopraisal.lowestPrice; }
  public get overAskingCount() { return this.zoopraisal.overAskingCount; }

  get isAvailable() {
    return this.zoopraisal.datasetCount >= 3;
  }
}

interface ZoopraisalParams {
  listing: Listing;
  monthsAgo?: number;
  distance?: number;
  statusId?: number;
}

export default async function getZoopraisal({ listing, monthsAgo = 6, distance = 1000, statusId = STATUS_ID_NOT_AVAILABLE_SOLD }: ZoopraisalParams) {
  const { position, homeType, bedrooms, bathrooms, avgSqft, isRental } = listing;

  if (
    isRental === true ||
    !homeType ||
    !position?.coordinates ||
    position.coordinates.length !== 2
  ) {
    return null;
  }


  const coordinates: ZoopraisalRequest_Coordinates = {
    coordinates: position?.coordinates,
    type: 'Point',
  };

  const actualHomeType = homeType ?? '';
  const actualSqft = avgSqft ?? 0;
  const actualBedrooms = bedrooms ?? 0;
  const actualBathrooms = bathrooms ?? 0;


  const host = getInsightsHost(isServerSide());
  const zoopraisalUrl = `${host}/insights/zoopraisal`;

  try {
    const zoopraisalRequest = ZoopraisalRequest.fromPartial({ position: coordinates, homeType: actualHomeType, bedrooms: actualBedrooms, bathrooms: actualBathrooms, sqft: actualSqft, monthsAgo: monthsAgo, distance: distance, statusId: statusId });
    const requestBody = ZoopraisalRequest.encode(zoopraisalRequest).finish();
    const response = await fetchWithRetry(zoopraisalUrl, { method: 'POST', body: requestBody });
    const content = await response.blob();
    const buffer = await content.arrayBuffer();
    const {
      averageDaysOnMarket,
      averagePrice,
      datasetAddresses,
      datasetCount,
      highestPrice,
      lowestPrice,
      overAskingCount,
    } = ZoopraisalResponse.decode(new Uint8Array(buffer));

    const zoopraisal = new ZoopraisalData({
      averageDaysOnMarket,
      averagePrice,
      datasetAddresses,
      datasetCount,
      highestPrice,
      lowestPrice,
      overAskingCount,
      isAvailable: datasetCount >= 3,
    });

    return zoopraisal.isAvailable ? zoopraisal : null;
  } catch (error) {
    console.error('Failed to fetch zoopraisal: %s', error);
    return null;
  }
}